// Фразы и описания, которые им соответствуют.
//
// Это заготовка демонстрации по варианту «запись-воспроизведение»: сейчас
// список читает демо-страница, позже его же будет отдавать `llm-gate` в режиме
// фикстур — фраза на входе, готовое описание на выходе, без обращения к модели.
// Поэтому файл лежит рядом с описанием, а не внутри страницы: у демонстрации и
// у шлюза должен быть один источник, иначе они разойдутся.
//
// Каждое описание здесь — то, что обязана вернуть модель на соответствующую
// фразу. Оно проходит тот же `compileViewSpec`, что и настоящий ответ: если
// фикстура разошлась с каталогом, тест это покажет.
import type { ViewSpec } from './spec.js';

export interface ViewFixture {
  /** Фраза пользователя — по ней ищется описание. */
  prompt: string;
  spec: ViewSpec;
}

export const VIEW_FIXTURES: readonly ViewFixture[] = [
  {
    prompt: 'Покажи три последних договора: сумма, фирма и дата',
    spec: {
      version: 1,
      title: 'Три последних договора',
      blocks: [
        {
          kind: 'table',
          entity: 'document',
          columns: ['title', 'amount', 'company', 'createdAt'],
          filters: [{ field: 'type', op: 'eq', value: 'contract' }],
          match: 'all',
          sort: { field: 'createdAt', direction: 'desc' },
          limit: 3,
        },
      ],
    },
  },
  {
    prompt: 'Сумма договоров по фирмам за последние 90 дней',
    spec: {
      version: 1,
      title: 'Договоры по фирмам',
      blocks: [
        {
          kind: 'stat',
          title: 'Всего договоров за период',
          entity: 'document',
          agg: 'sum',
          field: 'amount',
          filters: [
            { field: 'type', op: 'eq', value: 'contract' },
            { field: 'createdAt', op: 'lastDays', value: 90 },
          ],
          match: 'all',
        },
        {
          kind: 'group',
          entity: 'document',
          by: 'company',
          agg: 'sum',
          field: 'amount',
          filters: [
            { field: 'type', op: 'eq', value: 'contract' },
            { field: 'createdAt', op: 'lastDays', value: 90 },
          ],
          match: 'all',
          direction: 'desc',
          limit: 25,
        },
      ],
    },
  },
  {
    prompt: 'Задачи со сроком в ближайшую неделю — календарём',
    spec: {
      version: 1,
      title: 'Задачи на неделю',
      blocks: [
        {
          kind: 'calendar',
          entity: 'task',
          dateField: 'dueAt',
          filters: [
            { field: 'dueAt', op: 'nextDays', value: 7 },
            { field: 'status', op: 'in', value: ['todo', 'in_progress', 'blocked'] },
          ],
          match: 'all',
          limit: 100,
        },
      ],
    },
  },
  {
    prompt: 'Сколько у кого незакрытых задач',
    spec: {
      version: 1,
      title: 'Незакрытые задачи по исполнителям',
      blocks: [
        {
          kind: 'group',
          entity: 'task',
          by: 'assigneeMember',
          agg: 'count',
          filters: [{ field: 'status', op: 'in', value: ['todo', 'in_progress', 'blocked'] }],
          match: 'all',
          direction: 'desc',
          limit: 25,
        },
      ],
    },
  },
];

/**
 * «Хочу видеть договор, его ближайшие задачи и заметки к нему».
 *
 * Описание собирается вокруг конкретной записи, поэтому оно не константа: в
 * настоящем разговоре идентификатор приходит из контекста («вот этот договор»),
 * а на демо-странице — из выбора в списке.
 *
 * Заметки цепляются через таблицу связей: у заметки привязок может быть
 * несколько, колонкой на записи это не выражается.
 */
export function documentDossierSpec(documentId: string, title = 'Договор'): ViewSpec {
  return {
    version: 1,
    title,
    blocks: [
      {
        kind: 'record',
        entity: 'document',
        id: documentId,
        fields: ['title', 'type', 'status', 'amount', 'company', 'contact', 'createdAt'],
      },
      {
        kind: 'table',
        title: 'Ближайшие задачи',
        entity: 'task',
        columns: ['title', 'status', 'priority', 'dueAt', 'assigneeMember'],
        filters: [
          { field: 'document', op: 'eq', value: documentId },
          { field: 'status', op: 'in', value: ['todo', 'in_progress', 'blocked'] },
        ],
        match: 'all',
        sort: { field: 'dueAt', direction: 'asc' },
        limit: 3,
      },
      {
        kind: 'table',
        title: 'Заметки',
        entity: 'note',
        columns: ['body', 'createdByMember', 'createdAt'],
        filters: [{ field: 'document', op: 'eq', value: documentId }],
        match: 'all',
        sort: { field: 'createdAt', direction: 'desc' },
        limit: 10,
      },
    ],
  };
}
